import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface DocumentType {
  id: number
  name: string
}

interface DocumentForm {
  id: number
  name: string
  document_date: string | null
  customer_document_type_id: number | null
  notes: string
  filename: string | null
  has_file: boolean
}

interface Props {
  customer: Customer
  document: DocumentForm
  document_types: DocumentType[]
  errors?: Record<string, string[]>
}

export default function Edit({
  customer,
  document: doc,
  document_types,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Documents", href: `/customers/${customer.id}/documents` },
    {
      title: "Edit",
      href: `/customers/${customer.id}/documents/${doc.id}/edit`,
    },
  ]

  const { data, setData, patch, processing, transform } = useForm({
    name: doc.name ?? "",
    document_date: doc.document_date ?? "",
    customer_document_type_id: doc.customer_document_type_id ?? "",
    notes: doc.notes ?? "",
  })

  transform((data) => ({ customer_document: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/customers/${customer.id}/documents/${doc.id}`)
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit Document - ${customer.display_name}`} />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Edit Document</h1>

        {doc.has_file && doc.filename && (
          <p className="text-hpi-med-gray mb-4 text-sm">File: {doc.filename}</p>
        )}

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          <div>
            <label className={labelClass}>Date</label>
            <input
              type="date"
              value={data.document_date ?? ""}
              onChange={(e) => setData("document_date", e.target.value)}
              className={inputClass}
            />
            {errors?.document_date && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.document_date[0]}
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>Type</label>
            <select
              value={data.customer_document_type_id ?? ""}
              onChange={(e) =>
                setData(
                  "customer_document_type_id",
                  e.target.value ? e.target.value : "",
                )
              }
              className={inputClass}
            >
              <option value="">-- Select Type --</option>
              {document_types.map((dt) => (
                <option key={dt.id} value={dt.id}>
                  {dt.name}
                </option>
              ))}
            </select>
            {errors?.customer_document_type_id && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.customer_document_type_id[0]}
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>Brief Description</label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              maxLength={50}
              className={inputClass}
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">{errors.name[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={4}
              className={inputClass}
            />
            {errors?.notes && (
              <p className="text-hpi-red mt-1 text-sm">{errors.notes[0]}</p>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <Link
              href={`/customers/${customer.id}/documents/${doc.id}`}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Saving..." : "Update Document"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
